import type { DetayliAnalizVerisi } from "@koclukpanel/ui";
import type { KonuSatiri } from "./konu-durumu";
import { onayBilgisi } from "./onay-kilidi-durumu";
/**
 * API ISTEMCISI — ogrenci uygulamasi
 *
 * Panelinkiyle ayni kalip: tek gecis noktasi, cerez her istekte,
 * hata govdesi tek yerde cozuluyor.
 *
 * Fark: bu uygulama CEVRIMDISI calisabilmeli. Ag hatasi ile sunucu
 * hatasi AYRILIYOR (`AgHatasi` vs `ApiHatasi`) — cunku ag hatasinda
 * kayit kuyrukta beklemeli, sunucu hatasinda kullaniciya soylenmeli.
 */

import type {
  DavetOnayMetni,
  GorevRengi,
  MesaiAraligi,
  HataTipi,
  SenkronSonucu,
  KendiKodlamam,
  OyunOgrenciOzeti,
  PuanHareketiOzeti,
  DenemeHedefFarki,
  HedefNetYaniti,
} from "@koclukpanel/contracts";
import { cevrimdisiOturum, okumaOturumunuDogrula, oturumluOku } from "./pwa-onbellek";
import { oturum } from "./oturum-durumu";

// G-AUT-08b — davet/sıfırlama oturum açmaz; mevcut jeton uçları kullanılır.
// F-LGL-03a (24.10.2026) — aktivasyonda zorunlu metinler sunucudan gelir; 18 yaş altında liste boş.
export function davetOnayMetinleri(jeton: string): Promise<{ metinler: DavetOnayMetni[] }> {
  return api("/auth/davet/onay-metinleri", { yontem: "POST", govde: { jeton } });
}

export function davetiTamamla(
  jeton: string,
  sifre: string,
  onaylar: Array<{ tur: string; surum: string }> = [],
): Promise<{ durum: "AKTIF" | "MUVAFAKAT_BEKLIYOR" }> {
  return api("/auth/daveti-tamamla", { yontem: "POST", govde: { jeton, sifre, onaylar } });
}

export function sifreSifirla(jeton: string, sifre: string): Promise<unknown> {
  return api("/auth/sifre-sifirla", { yontem: "POST", govde: { jeton, sifre } });
}

export class ApiHatasi extends Error {
  constructor(
    readonly durum: number,
    readonly kod: string,
    mesaj: string,
    /**
     * Sunucunun `X-Istek-Id` başlığı — bir tıklamanın doğurduğu bütün
     * denetim ve log satırlarını birbirine bağlayan kimlik.
     *
     * Ölçüldü (05.09.2026): sunucu bu başlığı HER yanıta koyuyor
     * (`context.middleware.ts:35`), `Hata500.dc.html` çizimi ekranda
     * göstermeyi açıkça istiyor (*"Sorun sürerse bu kimliği iletin"*)
     * — ve **hiçbir istemci okumuyordu**, panel de dahil.
     *
     * Sonucu: bir şey bozulduğunda öğrencinin söyleyebileceği tek şey
     * "bir hata aldım" oluyordu. Sunucuda o isteği bulmanın yolu yok.
     */
    readonly istekId: string | null = null,
    /** G-AUT-08b — şifre politikasının alan hatası jeton formuna taşınır. */
    readonly ayrinti?: Record<string, string>,
  ) {
    super(mesaj);
    this.name = "ApiHatasi";
  }

  /**
   * Sunucu tarafı bir arıza mı (5xx).
   *
   * İstek kimliği YALNIZCA bunlarda gösteriliyor: 4xx'te mesaj zaten
   * kullanıcının yapabileceği bir şeyi anlatıyor ("kod yanlış",
   * "yetkin yok") ve teknik bir kimlik eklemek gürültü olurdu.
   * 5xx'te kullanıcının yapabileceği bir şey yok — tek işe yarar
   * bilgi o kimlik.
   */
  get sunucuArizasiMi(): boolean {
    return this.durum >= 500;
  }

  get onayBekliyorMu(): boolean {
    return this.durum === 403 && this.kod === "ONAY_BEKLIYOR";
  }

  get oturumBittiMi(): boolean {
    return this.durum === 401;
  }

  /**
   * Bakım modu — F-SEC-06.
   *
   * Ölçüldü 05.09.2026: bakım açıkken sunucu muaf olmayan her uca
   * `503 BAKIM_MODU` dönüyor. Bu uygulamada bakımı bilen TEK BİR SATIR
   * yoktu; öğrenci her ekranda genel hata görüyordu.
   */
  get bakimModuMu(): boolean {
    return this.durum === 503 && this.kod === "BAKIM_MODU";
  }
}

/** Sunucuya ULASILAMADI — kayit kuyrukta kalmali. */
export class AgHatasi extends Error {
  constructor() {
    super("Bağlantı yok.");
    this.name = "AgHatasi";
  }
}

const KOK = "/api/v1";

interface IstekSecenekleri {
  yontem?: "GET" | "POST" | "PATCH" | "DELETE";
  govde?: unknown;
  sorgu?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
  /** Sahiplik kanıtı bayat önbellekten alınamaz (F-STV-11). */
  taze?: boolean;
}

export async function api<T>(yol: string, o: IstekSecenekleri = {}): Promise<T> {
  const istek = oturum.istekBaslat();
  const iptal = () => istek.denetleyici.abort();
  if (o.signal?.aborted) iptal();
  o.signal?.addEventListener("abort", iptal, { once: true });
  try {
    if (yol === "/auth/ben" && oturum.kapali)
      return { girisYapildi: false, userId: null, izinler: {}, roller: [] } as T;
    let veri: T = await istekYap<T>(yol, { ...o, signal: istek.denetleyici.signal });
    istek.dogrula();
    if (yol === "/auth/ben") {
      oturum.dogrula(veri as Ben);
      istek.dogrula();
      veri = onayBilgisi.benYanitiniIsle(veri as Ben, true) as T;
      await okumaOturumunuDogrula(veri as Ben);
      istek.dogrula();
    }
    if (yol.startsWith("/auth/giris") && veri && typeof veri === "object" && "userId" in veri)
      oturum.degistir(false);
    if (yol === "/auth/gorunum" || yol === "/auth/gorunum/bitir") oturum.degistir(false);
    return veri;
  } catch (hata) {
    istek.dogrula();
    if (yol === "/auth/ben" && !o.taze && hata instanceof AgHatasi) {
      const onceki = cevrimdisiOturum();
      if (onceki) return onayBilgisi.benYanitiniIsle(onceki, false) as T;
    }
    if (hata instanceof ApiHatasi && hata.oturumBittiMi && !yol.startsWith("/auth/giris"))
      oturum.degistir(true);
    if (hata instanceof ApiHatasi && hata.onayBekliyorMu && typeof window !== "undefined") {
      onayBilgisi.kilitle(oturum.kullaniciId);
      window.dispatchEvent(new Event("onay-kilidi"));
    }
    throw hata;
  } finally {
    istek.bitir();
    o.signal?.removeEventListener("abort", iptal);
  }
}

async function istekYap<T>(yol: string, o: IstekSecenekleri): Promise<T> {
  const sorgu = o.sorgu
    ? "?" +
      new URLSearchParams(
        Object.entries(o.sorgu)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => [k, String(v)]),
      ).toString()
    : "";

  let yanit: Response;
  try {
    yanit = await oturumluOku(
      KOK + yol + sorgu,
      {
        method: o.yontem ?? "GET",
        credentials: "include",
        signal: o.signal,
        cache: "no-store",
        /*
        ÇOK PARÇALI GÖVDE (`FormData`) KENDİ BAŞLIĞINI ÜRETİR ve
        `Content-Type`ı ELLE YAZMAK BOZAR: tarayıcı sınır dizesini
        (boundary) kendisi ekliyor, biz `multipart/form-data` yazarsak
        o sınır kaybolur ve sunucu gövdeyi hiç çözemez.

        Dosya yükleme bu yüzden uzun süre AYRI bir `fetch` ile
        yapılıyordu; ayrı `fetch` ise `api()` içindeki hata çevirisini,
        401 davranışını ve istek kimliğini KAÇIRIYOR — bu depoda
        ölçülmüş bir hata sınıfı (`api-tek-kapi.test.ts`).
      */
        headers:
          o.govde !== undefined && !(o.govde instanceof FormData)
            ? { "Content-Type": "application/json" }
            : undefined,
        body:
          o.govde === undefined
            ? undefined
            : o.govde instanceof FormData
              ? o.govde
              : JSON.stringify(o.govde),
      },
      !o.taze,
    );
  } catch {
    // `fetch` yalnizca AG seviyesinde patlar. 500 bile buraya dusmez —
    // o yuzden bu dal kesin olarak "sunucuya ulasilamadi" demek.
    throw new AgHatasi();
  }

  if (yanit.status === 204) return undefined as T;

  const metin = await yanit.text();
  let veri: unknown = null;
  try {
    veri = metin ? JSON.parse(metin) : null;
  } catch {
    veri = null;
  }

  if (!yanit.ok) {
    const g = (veri ?? {}) as { kod?: string; mesaj?: string; ayrinti?: Record<string, string> };
    throw new ApiHatasi(
      yanit.status,
      g.kod ?? "BILINMEYEN",
      g.mesaj ?? `Sunucu ${yanit.status} döndü.`,
      // Başlık adı sunucudaki `context.middleware.ts` ile aynı olmalı.
      yanit.headers.get("X-Istek-Id"),
      g.ayrinti,
    );
  }

  return veri as T;
}

// =====================================================================
// Oturum
// =====================================================================

export interface Ben {
  onayKilidi?: { kilitli: boolean; kimVerir: "KENDISI" | "VELI"; eksikler: string[] } | null;
  gorunumde?: boolean;
  onayKilidiDevreDisi?: boolean;
  girisYapildi: boolean;
  userId: string | null;
  roller: string[];
  izinler: Record<string, string>;
  /**
   * ================================================================
   * HANGI EKRAN — ROLDEN TURETILMIYOR, SUNUCU SOYLUYOR
   * ================================================================
   * Bu uygulama artik iki kisiye birden hizmet ediyor: ogrenci ve
   * veli. Hangisinin ekranini cizecegini rol adina bakarak secmek
   * CLAUDE.md 1. maddeye aykiri olurdu ve kirilgan: roller degisken.
   *
   * Sunucu iki AYRI bayrak gonderiyor (`auth.controller.ts`), cunku
   * ayni kisi ikisi birden olabiliyor — mezun bir ogrenci kardesine
   * veli olabilir. Tek bir "tur" alani onu bir tarafa yuvarlardi.
   *
   * PERSONEL BAYRAGI YOK ve bilerek: ikisi de `false` ise personeldir
   * ve bu uygulamaya hic gelmemesi gerekir. Ucuncu bir alan olsaydi
   * uc bayrak birbiriyle tutarsiz hale gelebilirdi.
   */
  ogrenciMi: boolean;
  veliMi: boolean;
}

export function benGetir(taze = false): Promise<Ben> {
  return api<Ben>("/auth/ben", { taze });
}

export type GirisSonucu =
  | { ikinciAsama: false; userId: string }
  | { ikinciAsama: true; asamaJetonu: string; kalanSaniye: number };

export async function girisYap(email: string, sifre: string): Promise<GirisSonucu> {
  const y = await api<
    { userId: string } | { ikinciAsama: true; asamaJetonu: string; kalanSaniye: number }
  >("/auth/giris", { yontem: "POST", govde: { email, sifre } });
  if ("ikinciAsama" in y) return y;
  return { ikinciAsama: false, userId: y.userId };
}

export function ikinciAsama(
  asamaJetonu: string,
  kod: string,
  /** G-AUT-01c — "Bu cihazi 30 gun hatirla". Varsayilan FALSE. */
  cihaziHatirla = false,
): Promise<{ userId: string; kalanYedekKod: number | null }> {
  return api<{ userId: string; kalanYedekKod: number | null }>("/auth/giris/ikinci-asama", {
    yontem: "POST",
    govde: { asamaJetonu, kod, cihaziHatirla },
  });
}

/** G-AUT-01c — kullanicinin kendi guvenilir cihazlari. */
export interface GuvenilirCihaz {
  id: string;
  label: string | null;
  ipAddress: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string;
}

export function guvenilirCihazlar(): Promise<GuvenilirCihaz[]> {
  return api<GuvenilirCihaz[]>("/auth/guvenilir-cihazlar");
}

export function guvenilirCihazKaldir(cihazId: string): Promise<{ kaldirildi: boolean }> {
  return api<{ kaldirildi: boolean }>(`/auth/guvenilir-cihazlar/${cihazId}/kaldir`, {
    yontem: "POST",
  });
}

/**
 * Butun guvenilir cihazlari dusurur — G-AUT-01c kural 6.
 *
 * TELEFONUNU KAYBEDEN OGRENCININ ILK IHTIYACI. Tek tek kaldirma,
 * elinde olmayan cihazin listede hangisi oldugunu bilmesini
 * gerektiriyor; kaybeden kisi bunu bilemez.
 */
export function guvenilirCihazlariKaldir(): Promise<{ kaldirilan: number }> {
  return api<{ kaldirilan: number }>("/auth/guvenilir-cihazlar/hepsini-kaldir", {
    yontem: "POST",
  });
}

/**
 * Şifre sıfırlama bağlantısı ister — G-AUT-08.
 *
 * ================================================================
 * DÖNEN MESAJ AYNEN GÖSTERİLİYOR
 * ================================================================
 * Sunucu, adres KAYITLI OLSUN OLMASIN aynı yanıtı veriyor (G-AUT-06):
 * aksi hâlde bu uç, hangi adreslerin sistemde olduğunu öğrenmek için
 * kullanılabilirdi. Arayüz kendi cümlesini uydurmamalı — "gönderildi"
 * demek, kayıtlı olmayan bir adres yazan kişiye yanlış bilgi vermek
 * olur; "bulunamadı" demek ise sunucunun özenle sakladığı şeyi
 * ekrandan sızdırmak.
 */
export function sifirlamaIste(email: string): Promise<{ mesaj: string }> {
  return api<{ mesaj: string }>("/auth/sifre-sifirlama-istegi", {
    yontem: "POST",
    govde: { email },
  });
}

/** G-SES-03 — kullanıcının kendi açık oturumları. */
export interface OturumOzeti {
  id: string;
  ipAddress: string | null;
  userAgent: string | null;
  platform: string | null;
  deviceLabel: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  /**
   * Bu ekranı açan oturumun kendisi mi — SUNUCUDAN gelir.
   *
   * Arayüz TAHMİN EDEMEZ: oturum çerezi `httpOnly` ve JavaScript onu
   * okuyamıyor. Tahmin edilseydi öğrenciye "diğerlerini kapat"
   * dedirtip kendi oturumunu kapattırmak an meselesiydi.
   */
  suAnki: boolean;
}

export function oturumlarim(): Promise<OturumOzeti[]> {
  return api<OturumOzeti[]>("/auth/oturumlar");
}

/**
 * Tek oturumu sonlandırır.
 *
 * Kullanıcı kimliği gövdede DEĞİL, sunucu oturumdan okuyor: başkasının
 * oturum kimliği yazılsa da `where` eşleşmiyor ve hiçbir şey kapanmıyor.
 */
export function oturumSonlandir(sessionId: string): Promise<{ kapatildi: boolean }> {
  return api<{ kapatildi: boolean }>(`/auth/oturumlar/${encodeURIComponent(sessionId)}/sonlandir`, {
    yontem: "POST",
  });
}

/**
 * Kendi şifresini değiştirir — G-AUT-08a. Hata metni politika ayrıntısını
 * doğrudan taşıyor (bu uygulamanın hata sınıfında `ayrinti` yok).
 */
export function sifreDegistir(govde: {
  mevcutSifre: string;
  yeniSifre: string;
  digerOturumlariSonlandir: boolean;
}): Promise<{ kapatilanOturum: number }> {
  return api<{ kapatilanOturum: number }>("/auth/sifre-degistir", { yontem: "POST", govde });
}

export function digerOturumlariSonlandir(): Promise<{ kapatilan: number }> {
  return api<{ kapatilan: number }>("/auth/oturumlar/digerlerini-sonlandir", { yontem: "POST" });
}

export async function cikisYap(): Promise<void> {
  // F-AUTH-07 — çıkış başarısız olsa da eski ekran yeniden açılmamalı.
  oturum.degistir(true);
  await api<void>("/auth/cikis", { yontem: "POST" });
}

/**
 * Ogrencinin kendi kaydi.
 *
 * ================================================================
 * TIP DARDI VE VERI SESSIZCE DUSUYORDU — 04.09.2026
 * ================================================================
 * Burada uc alan yaziliydi: `{ id, studentNo, status }`. Sunucu ise
 * `GET /ogrenciler/ben` ucundan cok daha fazlasini DONUYORDU
 * (`kisiler.service.ts` -> `ozete()`): ad, soyad, sinif ve muvafakat
 * bayragi.
 *
 * Sonuc: ogrenci uygulamasi kullanicinin ADINI hic goremiyordu ve
 * "Bugun" ekraninin karsilamasi ("Merhaba Deniz") cizilemiyordu.
 * Sunucu degisikligi degil, YALNIZCA bu tipin darligiydi.
 *
 * Bu, depodaki tekrar eden hata sinifinin bir yuzu: veri VAR ama onu
 * kullanan yol yok. `api.ts` bir sozlesme kopyasi tutuyor ve kopya
 * eskidiginde hicbir sey bagirmiyor — TypeScript fazladan alanlari
 * sessizce dusuruyor, cunku daralma bir hata degil.
 */
export interface KendiOgrenciKaydi {
  id: string;
  /** `Student.studentNo` otomatik artan bir SAYI, dizgi degil */
  studentNo: number;
  status: string;
  ad: string;
  soyad: string;
  sinif: string;
  /** 18 yas alti ve veli muvafakati henuz alinmamis */
  muvafakatBekliyor: boolean;
}

export function kendiKaydim(): Promise<KendiOgrenciKaydi> {
  return api<KendiOgrenciKaydi>("/ogrenciler/ben");
}

// =====================================================================
// Gorevler — F-STV
// =====================================================================

/**
 * ================================================================
 * TIP YINE DARDI — DOKUZ ALAN SESSIZCE DUSUYORDU (04.09.2026)
 * ================================================================
 * Canli olculdu: `GET /calisma/ogrenci/:id/bugun` OTUZ ALTI alan
 * donuyor, bu arayuz YIRMI SEKIZINI biliyordu. Eksik dokuz:
 * `scheduleId`, `testFrom`, `testTo`, `targetTestCount`,
 * `videoTitle`, `missNote`, `needsReview`, `postponedToDate`,
 * `editedByCoachAt`.
 *
 * Bu, `KendiOgrenciKaydi`nin ustundeki notta anlatilan hata
 * sinifinin AYNISI ve dosyada IKINCI kez cikti: veri telden geliyor,
 * tip bildirmedigi icin hicbir ekran erisemiyor. TypeScript bunu
 * hata saymiyor — fazladan alani dusurmek daralmadir ve daralma
 * gecerlidir. Yani derleyici, lint ve testlerin ucu de sessiz.
 *
 * Somut kaybi olan ikisi:
 * - `missNote`: "Yapamadim"da ZORUNLU olarak yazdirilan aciklama.
 *   Ogrenci yazdiktan sonra bir daha goremiyordu.
 * - `testFrom`/`testTo`/`targetTestCount`: uzman gorevi test numarasi
 *   ile verirse (`program.controller.ts` bunlari kabul ediyor) kart
 *   yalnizca `pageFrom/pageTo`ya baktigi icin ogrenci hangi testi
 *   cozecegini HIC gormuyor.
 */
export interface Gorev {
  id: string;
  scheduleId: string;
  itemDate: string;
  sortOrder: number;
  kind: string;
  lessonId: string;
  topicId: string | null;
  pageFrom: number | null;
  pageTo: number | null;
  testFrom: number | null;
  testTo: number | null;
  targetQuestionCount: number | null;
  targetTestCount: number | null;
  startTime: string | null;
  youtubeUrl: string | null;
  videoTitle: string | null;
  topicPdfUrl: string | null;
  testPdfUrl: string | null;
  coachNote: string | null;
  isExcused: boolean;
  status: string;
  correctCount: number | null;
  wrongCount: number | null;
  emptyCount: number | null;
  durationSeconds: number | null;
  difficultyRating: number | null;
  studentNote: string | null;
  missReason: string | null;
  /** "Yapamadim" ekraninda yazilan aciklama — OTHER secilirse zorunlu */
  missNote: string | null;
  needsReview: boolean;
  completedAt: string | null;
  /** Gorev baska bir gune ertelendiyse o gun */
  postponedToDate: string | null;
  /** Uzman ogrencinin verisini duzelttiyse damga */
  editedByCoachAt: string | null;
  lesson: { name: string };
  topic: { name: string } | null;
  gosterilecekDurum: string;
  timerCalisiyor: boolean;
  /**
   * Gorevin kaynak kitabi (A5). Istege bagli: veri girisi yaniti
   * (`girisKaydet`) bu alani tasimiyor, yalniz okuma uclari tasiyor.
   */
  kaynakAdi?: string | null;
  /** F-STV-02a — sunucunun hesapladigi renk; istemci yeniden hesaplamaz. */
  renk?: GorevRengi;
  /** F-STV-04a — ogrenci sonucu gunu gectikten sonra girdi ("sonradan girildi"). */
  lateEnteredAt?: string | null;
}

export function bugunkuGorevler(ogrenciId: string): Promise<Gorev[]> {
  return api<Gorev[]>(`/calisma/ogrenci/${ogrenciId}/bugun`);
}

export function haftalikGorevler(ogrenciId: string, tarih: string): Promise<Gorev[]> {
  return api<Gorev[]>(`/calisma/ogrenci/${ogrenciId}/hafta`, { sorgu: { tarih } });
}

export interface SayacOturumu {
  id: string;
  startedAt: string;
  zatenAcikti: boolean;
}

export function sayacBaslat(gorevId: string): Promise<SayacOturumu> {
  return api<SayacOturumu>(`/calisma/gorevler/${gorevId}/sayac/basla`, { yontem: "POST" });
}

export function sayacKalp(oturumId: string): Promise<{ alindi: true }> {
  return api<{ alindi: true }>(`/calisma/sayac/${oturumId}/kalp`, { yontem: "POST" });
}

export function sayacBitir(
  oturumId: string,
): Promise<{ saniye: number; terkEdilmis: boolean; zatenKapali: boolean }> {
  return api(`/calisma/sayac/${oturumId}/bitir`, { yontem: "POST" });
}

// =====================================================================
// Cevrimdisi senkron — F-STV-11
// =====================================================================

// =====================================================================
// Deneme — F-EXM
// =====================================================================

export interface DenemeBolumu {
  id: string;
  name: string;
  questionCount: number;
}

export type PencereDurumu = "ACIK" | "HENUZ_ACILMADI" | "KAPANDI";

export interface DenemeOzeti {
  atamaId: string;
  id: string;
  name: string;
  kind: string;
  examDate: string;
  startsAt: string;
  endsAt: string;
  pdfUrl: string | null;
  bolumler: DenemeBolumu[];
  /** Pencere durumu SUNUCUDAN — arayüz kendi saatine göre hesaplamıyor */
  pencere: PencereDurumu;
  /**
   * `ExamAssignment.participation` — uzmanın işaretlediği katılım.
   *
   * ================================================================
   * BU ALAN 04.09.2026'DA TELE ÇIKTI, TİPE HEMEN YAZILDI
   * ================================================================
   * Denetimde (C14.6) bulunan eksikti: sunucu `participation`ı
   * sorguluyor ama yanıta koymuyordu, bu yüzden uzmanın `MISSED`
   * işaretlediği deneme öğrencinin listesinde HENÜZ KODLAMADIĞI bir
   * denemeden ayırt edilemiyordu. Ana oturum sunucu tarafını düzeltti;
   * tip burada yazılmasaydı veri yine erişilemez kalır ve aynı bulgu
   * bir kez daha üretilirdi — C14.6'nın anlattığı sınıf tam olarak bu.
   */
  katilim: "ASSIGNED" | "IN_PROGRESS" | "COMPLETED" | "MISSED";
  girildiMi: boolean;
  sonuc: {
    totalNet: number;
    totalCorrect: number;
    totalWrong: number;
    totalEmpty: number;
  } | null;
}

export function denemelerim(): Promise<DenemeOzeti[]> {
  return api<DenemeOzeti[]>("/denemeler/bana-atananlar");
}

export function kendiKodlamam(denemeId: string): Promise<KendiKodlamam> {
  return api(`/denemeler/${denemeId}/kodlamam`);
}

export interface KodlamaSonucu {
  atamaId: string;
  toplam: { dogru: number; yanlis: number; bos: number; net: number };
  puan: number | null;
  bolumler: Array<{
    sectionId: string;
    correct: number;
    wrong: number;
    empty: number;
    net: number;
  }>;
  sureSupheli: boolean;
}

export function denemeKodla(
  denemeId: string,
  cevaplar: Record<string, string>,
  sureDakika: number | null,
): Promise<KodlamaSonucu> {
  return api<KodlamaSonucu>(`/denemeler/${denemeId}/kodlama`, {
    yontem: "POST",
    govde: { cevaplar, sureDakika },
  });
}

export interface SenkronKaydiGonderim {
  istemciKayitId: string;
  islem: string;
  govde: Record<string, unknown>;
  yapildiAt: string;
}

export function senkronGonder(
  sahipKullaniciId: string,
  kayitlar: readonly SenkronKaydiGonderim[],
): Promise<SenkronSonucu> {
  return api<SenkronSonucu>("/senkron", { yontem: "POST", govde: { sahipKullaniciId, kayitlar } });
}

// =====================================================================
// Program değişiklik talebi — F-SCH-11
// =====================================================================

export type DegisiklikNedeni =
  | "SCHOOL_LOAD"
  | "HEALTH"
  | "FAMILY"
  | "DID_NOT_UNDERSTAND"
  | "RESOURCE_ISSUE"
  | "TOO_HEAVY"
  | "TOO_LIGHT"
  | "OTHER";

export type TalepDurumu = "PENDING" | "ACCEPTED" | "PARTIALLY_ACCEPTED" | "REJECTED" | "CANCELLED";

/**
 * ================================================================
 * HAK DURUMU SUNUCUDAN GELİR
 * ================================================================
 * Ekran "bu ay talep açtım mı" sorusunu KENDİ takvimine göre
 * hesaplamıyor. Hesaplasaydı saati yanlış olan bir telefonda öğrenci
 * hakkını kullanamamış ya da fazladan kullanmış görünürdü — ve sınır
 * zaten veritabanı kısıtıyla uygulandığı için ekran yalan söylemiş
 * olurdu.
 */
export interface HakDurumu {
  normalKullanildi: boolean;
  /** Uzmanın tanıdığı, henüz harcanmamış ek hak yeri */
  kullanilabilirEkHak: number | null;
  acabilirMi: boolean;
  neden: "YOK" | "AY_DOLU";
  ay: string;
  /**
   * Yeni hakkın açılacağı an — SUNUCUDAN.
   *
   * Ekran bu tarihi KENDİ hesaplamıyor ve hesaplamamalı: bu dosyanın
   * ve `DegisiklikTalebi`nin açılış notu "ekran bu soruyu kendi
   * takvimine göre HESAPLAMIYOR" diyor, çünkü saati yanlış bir telefon
   * yanlış tarih gösterir ve kararı zaten sunucu veriyor.
   *
   * Hak dolu olmasa da her zaman dolu geliyor — sunucu bunu bilerek
   * koşulsuz döndürüyor.
   */
  sonrakiHakAt: string;
}

export function degisiklikHakkim(): Promise<HakDurumu> {
  return api<HakDurumu>("/program/degisiklik/hakkim");
}

export interface DegisiklikTalebi {
  id: string;
  reason: DegisiklikNedeni;
  reasonText: string;
  status: TalepDurumu;
  coachResponse: string | null;
  isExtraGrant: boolean;
  createdAt: string;
  decidedAt: string | null;
}

export function degisiklikTaleplerim(): Promise<{ toplam: number; talepler: DegisiklikTalebi[] }> {
  return api("/program/degisiklik");
}

export function degisiklikTalebiAc(veri: {
  reason: DegisiklikNedeni;
  reasonText: string;
  scheduleId?: string | null;
}): Promise<DegisiklikTalebi> {
  return api<DegisiklikTalebi>("/program/degisiklik", { yontem: "POST", govde: veri });
}

// =====================================================================
// Takvim ve ICS aboneliği — F-CAL
// =====================================================================

export type TakvimEtkinligi =
  | { mod: "MESGULIYET"; id: string; startsAt: string; endsAt: string; isAllDay: boolean }
  | {
      mod: "AYRINTI";
      id: string;
      startsAt: string;
      endsAt: string;
      isAllDay: boolean;
      type: string;
      title: string;
      description: string | null;
      location: string | null;
      meetUrl: string | null;
    };

export function takvimim(
  baslangic: string,
  bitis: string,
): Promise<{ etkinlikler: TakvimEtkinligi[] }> {
  return api("/takvim/benim", { sorgu: { baslangic, bitis } });
}

export function icsBaglantim(): Promise<{ adres: string; aktif: boolean }> {
  return api("/takvim/ics/baglantim");
}

export function icsYenile(): Promise<{ adres: string }> {
  return api("/takvim/ics/yenile", { yontem: "POST" });
}

// =====================================================================
// Talep kutusu ve destek (matris §5.8 / §5.15)
// =====================================================================

export interface TalepKategorisi {
  id: string;
  code: string;
  name: string;
  description: string | null;
  openableBy: "STUDENT" | "PARENT" | "BOTH";
}

/**
 * Talep kutusu durumu (`ApprovalStatus`).
 *
 * `TalepDurumu` adi PROGRAM DEGISIKLIGI talebine ait
 * (`ChangeRequestStatus`) ve ikisi ayri enum — ayni ada iki farkli
 * degerler kumesi koymak, birini digerinin yerine gecirmenin sessiz
 * yolunu acardi.
 */
export type OnayDurumu = "PENDING" | "IN_REVIEW" | "APPROVED" | "REJECTED" | "CANCELLED";

export interface Talebim {
  id: string;
  subject: string;
  body: string;
  status: OnayDurumu;
  decisionNote: string | null;
  createdAt: string;
  decidedAt: string | null;
  category: { code: string; name: string };
}

export function talepKategorileri(): Promise<TalepKategorisi[]> {
  return api<TalepKategorisi[]>("/operasyon/talep-kategorileri");
}

export function taleplerim(ogrenciId?: string): Promise<{ toplam: number; talepler: Talebim[] }> {
  return api("/operasyon/talepler", { sorgu: { ogrenci: ogrenciId } });
}

export function talepAc(veri: {
  ogrenciId?: string;
  kategoriId: string;
  konu: string;
  govde: string;
}): Promise<{ id: string; subject: string }> {
  return api("/operasyon/talepler", { yontem: "POST", govde: veri });
}

export type DestekKonusu = "TECHNICAL" | "HOW_TO" | "BUG" | "SUGGESTION" | "OTHER";
export type DestekDurumu = "OPEN" | "IN_REVIEW" | "ANSWERED" | "CLOSED";

export interface DestekOzeti {
  id: string;
  ticketNo: number;
  topic: DestekKonusu;
  subject: string;
  status: DestekDurumu;
  screenKey: string | null;
  createdAt: string;
  respondedAt: string | null;
}

export interface DestekMesaji {
  id: string;
  senderId: string;
  body: string;
  /** SUNUCUDA izinden türetilir — istemciden gelmez */
  isStaff: boolean;
  createdAt: string;
}

export function destekListem(): Promise<{
  toplam: number;
  yonetimGorunumu: boolean;
  talepler: DestekOzeti[];
}> {
  return api("/yardim/destek");
}

export function destekGetir(id: string): Promise<DestekOzeti & { mesajlar: DestekMesaji[] }> {
  return api(`/yardim/destek/${id}`);
}

export function destekMesajEkle(id: string, govde: string): Promise<{ id: string }> {
  return api(`/yardim/destek/${id}/mesaj`, { yontem: "POST", govde: { govde } });
}

export function destekAc(veri: {
  konu: DestekKonusu;
  baslik: string;
  mesaj: string;
  ekranAnahtari?: string | null;
}): Promise<{ id: string; ticketNo: number }> {
  return api("/yardim/destek", { yontem: "POST", govde: veri });
}

// =====================================================================
// YARDIM İÇERİĞİ — "Nasıl kullanılır" (F-HLP-09…13, aşama 1 05.10.2026)
// =====================================================================
// Panelle AYNI uçlar (`yardim/icerik`). Süzme sunucuda: öğrenci yalnızca
// öğrenci yazılarını, veli yalnızca veli yazılarını alır — istemci bir
// şey süzmüyor, süzseydi sunucuyu atlayan bir istek her şeyi görürdü.

export interface YardimYazisi {
  id: string;
  slug: string;
  baslik: string;
  kategori: string;
  govde: string;
  ekranAnahtari: string | null;
  sira: number;
}

export interface YardimBolumu {
  kategoriler: { ad: string; yazilar: YardimYazisi[] }[];
  toplam: number;
  ekranYazilari: YardimYazisi[];
}

export function yardimBolumu(): Promise<YardimBolumu> {
  return api("/yardim/icerik");
}

export function yardimAra(q: string): Promise<YardimYazisi[]> {
  return api("/yardim/icerik/ara", { sorgu: { q } });
}

/** Tek yazı — sunucu okunma sayacını artırır (F-HLP-13). */
export function yardimYazisi(slug: string): Promise<YardimYazisi> {
  return api(`/yardim/icerik/${encodeURIComponent(slug)}`);
}

// =====================================================================
// KVKK — rıza ve veri paketi (F-LGL-02)
// =====================================================================

export type RizaTuru =
  | "DATA_TRANSFER_ABROAD"
  | "WITHDRAWAL_RIGHT"
  | "PRIVACY_NOTICE"
  | "CONTACT_SMS"
  | "CONTACT_WHATSAPP"
  | "CONTACT_EMAIL"
  | "MARKETING"
  | "PROTECTED_NOTES"
  | "DISTANCE_SALES"
  | "PRE_INFO_FORM"
  | "PARENT_GUARANTEE";

export interface RizaSatiri {
  type: RizaTuru;
  documentVersion: string;
  granted: boolean;
  grantedAt: string;
  revokedAt: string | null;
  veliVerdi: boolean;
  /** Yürürlükteki sürüme ait mi — eski sürüme verilmiş onay geçersiz */
  guncelMi: boolean;
}

export interface RizaDurumu {
  userId: string;
  resitMi: boolean;
  /**
   * SUNUCUDAN gelir — ekran yaş hesabı YAPMIYOR.
   * 18 yaşını dolduran öğrencinin rızasını yalnızca kendisi yönetir
   * (matris §5.10).
   */
  rizayiKimVerir: "KENDISI" | "VELI";
  yururluktekiSurumler: Record<string, string>;
  rizalar: RizaSatiri[];
  engelleyenEksikler: RizaTuru[];
  eksikIstegeBagli: RizaTuru[];
  aktiflesebilirMi: boolean;
}

export function rizalarim(): Promise<RizaDurumu> {
  return api<RizaDurumu>("/kvkk/rizalarim");
}

export interface HukukiMetin {
  id: string;
  type: RizaTuru;
  version: string;
  title: string;
  body: string;
}

export function metinGetir(tur: RizaTuru): Promise<HukukiMetin> {
  return api<HukukiMetin>(`/kvkk/metinler/${tur}`);
}

/**
 * Sunucu bu yolu TANIMIYOR mu. Nest'in yol bulunamadı 404'ü gövdede `kod` taşımaz (`BILINMEYEN`e düşer); bizim
 * 404'lerimiz her zaman `kod` taşır (ör. `OGRENCI_DEGIL`). Yeni istemci eski API'ye bağlandığında (istemci önce
 * dağıtılınca ya da geri dönüşte) kişisel uçlar yok — eski yola düşülür (Codex tur 32 [P2]).
 */
export function eskiApiYoluYokMu(hata: unknown): boolean {
  return hata instanceof ApiHatasi && hata.durum === 404 && hata.kod === "BILINMEYEN";
}

export async function rizaVer(
  tur: RizaTuru,
  surum: string,
  hedefUserId?: string,
): Promise<{ id: string }> {
  if (hedefUserId) {
    return api<{ id: string }>("/kvkk/rizalar", {
      yontem: "POST",
      govde: { tur, surum, hedefUserId },
    });
  }
  try {
    return await api<{ id: string }>("/kvkk/rizalarim", { yontem: "POST", govde: { tur, surum } });
  } catch (hata) {
    if (!eskiApiYoluYokMu(hata)) throw hata;
    return api<{ id: string }>("/kvkk/rizalar", { yontem: "POST", govde: { tur, surum } });
  }
}

export function rizaDurumu(userId: string): Promise<RizaDurumu> {
  return api(`/kvkk/rizalar/${encodeURIComponent(userId)}`);
}

export async function rizaGeriCek(
  tur: RizaTuru,
  hedefUserId?: string,
): Promise<{ id: string; revokedAt: string }> {
  type Sonuc = { id: string; revokedAt: string };
  if (hedefUserId) {
    return api<Sonuc>("/kvkk/rizalar/geri-cek", { yontem: "POST", govde: { tur, hedefUserId } });
  }
  try {
    return await api<Sonuc>("/kvkk/rizalarim/geri-cek", { yontem: "POST", govde: { tur } });
  } catch (hata) {
    if (!eskiApiYoluYokMu(hata)) throw hata;
    return api<Sonuc>("/kvkk/rizalar/geri-cek", { yontem: "POST", govde: { tur } });
  }
}

/** Veri taşınabilirliği paketi — KVKK m.11 */
export function veriPaketi(ogrenciId: string): Promise<unknown> {
  return api(`/kvkk/veri-paketi/${ogrenciId}`);
}

/**
 * IKI ADIMLI DOGRULAMA — G-AUT-01, ogrenci/velide ISTEGE BAGLI
 *
 * Politika sunucudan geliyor; uygulama "ogrenciyse istege baglidir"
 * diye VARSAYMIYOR. Varsaysaydi, ileride bir ogrenci rolune
 * zorunluluk getirildiginde ekran hala kapatma dugmesi gosterirdi ve
 * sunucu reddederdi — kullanici icin "bozuk dugme".
 */
export interface IkiAsamaDurumu {
  etkinPolitika: "REQUIRED" | "OPTIONAL" | "DISABLED";
  kuruldu: boolean;
  kilitliRolVar: boolean;
  gecisKalanGun: number | null;
  kurulumZorunlu: boolean;
  kalanYedekKod: number;
}

export function ikiAsamaDurum(): Promise<IkiAsamaDurumu> {
  return api("/auth/2fa/durum");
}

export function ikiAsamaKurulumBaslat(): Promise<{
  gizliAnahtar: string;
  adres: string;
  qrVeriUrl: string;
}> {
  return api("/auth/2fa/kurulum", { yontem: "POST", govde: {} });
}

export function ikiAsamaKurulumTamamla(kod: string): Promise<{ yedekKodlar: string[] }> {
  return api("/auth/2fa/kurulum/tamamla", { yontem: "POST", govde: { kod } });
}

/** Kapatma da KOD istiyor — açık bir oturumu ele geçiren biri kaldıramasın. */
export function ikiAsamaKapat(kod: string): Promise<{ kapatildi: true }> {
  return api("/auth/2fa/kapat", { yontem: "POST", govde: { kod } });
}

export function yedekKodlariYenile(kod: string): Promise<{ yedekKodlar: string[] }> {
  return api("/auth/2fa/yedek-kod/yenile", { yontem: "POST", govde: { kod } });
}

// =====================================================================
// HATALI SORU DEFTERİM — F-MST
// =====================================================================

/**
 * Hata tipi — SÖZLEŞMEDEN.
 *
 * Burada aynı beş değer elle yazılıydı, yani enum'un ÜÇÜNCÜ eviydi
 * (şema · sözleşme · burası) ve üstelik SIRASI FARKLIYDI: `TIME`
 * üçüncü sıradaydı, sözleşmede sonuncu. Birlik tipinde sıra bir şey
 * ifade etmiyor, ama `HataDefterim`in ekranda kullandığı sıra bu
 * yüzden ayrıca elle yazılmak zorunda kalmıştı — kopya kopyayı
 * doğuruyordu.
 */
export type { HataTipi };

export interface HataKaydi {
  id: string;
  lessonId: string;
  topicId: string | null;
  errorType: HataTipi | null;
  studentNote: string | null;
  coachNote: string | null;
  resolvedAt: string | null;
  retryCount: number;
  lastRetryOk: boolean | null;
  createdAt: string;
  lesson?: { name: string } | null;
  topic?: { name: string } | null;
}

/** Katalog SUNUCUDAN — ekran kendi listesini uydurmuyor. */
export function hataTipleri(): Promise<Record<string, string>> {
  return api("/hata-defteri/hata-tipleri");
}

export function hataDefterim(ogrenciId: string, yalnizcaCozulmemis = false): Promise<HataKaydi[]> {
  return api(`/hata-defteri/ogrenci/${encodeURIComponent(ogrenciId)}`, {
    sorgu: { cozulmemis: yalnizcaCozulmemis ? "1" : undefined },
  });
}

export function hataEkle(veri: {
  ogrenciId: string;
  lessonId: string;
  topicId?: string | null;
  errorType?: HataTipi | null;
  studentNote?: string | null;
}): Promise<HataKaydi> {
  return api("/hata-defteri", { yontem: "POST", govde: veri });
}

/** İKİ kez üst üste doğru gerekiyor; yanlış bir tekrar sayacı sıfırlar. */
export function hataTekrar(id: string, dogruMu: boolean): Promise<HataKaydi> {
  return api(`/hata-defteri/${encodeURIComponent(id)}/tekrar`, {
    yontem: "POST",
    govde: { dogruMu },
  });
}

/** Kalıcı silme YOK — görünen "sil", yapılan arşivleme. */
export function hataKaldir(id: string): Promise<unknown> {
  return api(`/hata-defteri/${encodeURIComponent(id)}/kaldir`, { yontem: "POST" });
}

export interface KonumKonu {
  topicId: string;
  ad: string;
  ders: string;
  lessonId: string;
  /**
   * Arşivlenmiş konu listede KALIYOR (ilerleme geçmişi kaybolmasın)
   * ama YENİ kayıtta seçilemez — sunucu da reddediyor
   * (`akademik-secim.ts`). Süzmeyi çağıran ekran yapıyor.
   */
  arsivli: boolean;
}

/**
 * Kendi müfredat konularım — hata kaydederken ders ve konu seçimi
 * için. Öğrencide `akademik.mufredat.goruntule` izni YOK; müfredat
 * ağacına erişemiyor, kendi listesi tek kaynağı.
 */
export function konularim(ogrenciId: string): Promise<KonumKonu[]> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/konular`);
}

/**
 * Konularım ekranı — F-TPC-01b. AYNI uç, durum alanıyla. Öğrenci bu
 * listeyi yalnızca OKUR; değiştirme ucu öğrenciye 403 döner ve sunucu
 * geçiş listesini (`izinliGecisler`) öğrenciye boş gönderir.
 */
/**
 * Detaylı analiz — F-ANL-08b. Koçun panelde gördüğüyle AYNI uç ve AYNI
 * çizim (`@koclukpanel/ui` `DetayliAnaliz`): öğrenci ile koç aynı
 * haftaya bakıp farklı rakam görmesin.
 */
export function detayliAnalizim(ogrenciId: string, tarih: string): Promise<DetayliAnalizVerisi> {
  return api(`/analiz/ogrenci/${encodeURIComponent(ogrenciId)}/detay`, { sorgu: { tarih } });
}

export function konuIlerlemem(ogrenciId: string): Promise<KonuSatiri[]> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/konular`);
}

// =====================================================================
// BAKIM MODU — F-SEC-06
// =====================================================================

export interface BakimDurumu {
  acik: boolean;
  baslik: string | null;
  mesaj: string | null;
  bitisTahmini: string | null;
}

/**
 * Bakım durumu — KİMLİK İSTEMİYOR (`@HerkeseAcik()`).
 *
 * Bu yüzden giriş ekranında da çağrılabiliyor: öğrenci şifresini yazıp
 * içeri girdikten SONRA öğrenmesin.
 */
export function bakimDurumu(): Promise<BakimDurumu> {
  return api<BakimDurumu>("/bakim/durum");
}

// =====================================================================
// Veli portalı — F-PAR
// =====================================================================

export interface VeliCocuk {
  userId: string;
  id: string;
  studentNo: number;
  gradeLevel: string;
  ad: string;
}

export function veliCocuklarim(): Promise<VeliCocuk[]> {
  return api<VeliCocuk[]>("/veli/cocuklarim");
}

export interface VeliOzet {
  hafta: { basi: string; sonu: string };
  program: {
    atanan: number;
    tamamlanan: number;
    mazeretli: number;
    /** Ölçülemiyorsa `null` — arayüz bunu "%0" göstermemeli */
    tamamlamaOrani: number | null;
  };
  calisma: {
    toplamSureSaniye: number;
    /**
     * Veri girilmiş ama süresi girilmemiş görev sayısı — F-ANL.
     *
     * `durationSeconds` isteğe bağlı: öğrenci sayacı çalıştırmadan
     * yalnızca cevapları girerek görevi tamamlayabiliyor ve o görev
     * toplama **sıfır** katkıda bulunuyor.
     *
     * Toplam yanlış değil, **EKSİK** — ve eksik olduğu görünmüyor.
     * Sıfırdan büyükse ekran bunu yazmalı: "3 görevde süre girilmemiş".
     */
    sureGirilmeyenGorev: number;
    dogru: number;
    yanlis: number;
    basariOrani: number | null;
  };
  sonRapor: { id: string; periodStart: string; periodEnd: string } | null;
  /**
   * Son dört hafta, eskiden yeniye; son eleman BU hafta (dalga 3, F-PAR-03).
   * Görevsiz hafta atlanmıyor: oranı `null` (payda yok). İsteğe bağlı: eski
   * sunucu cevabında yok, o zaman trend çizilmiyor.
   */
  haftalar?: Array<{
    haftaBasi: string;
    haftaSonu: string;
    atanan: number;
    tamamlanan: number;
    mazeretli: number;
    tamamlamaOrani: number | null;
  }>;
}

export function veliOzet(ogrenciId: string): Promise<VeliOzet> {
  return api<VeliOzet>(`/veli/ogrenci/${ogrenciId}/ozet`);
}

export interface VeliRaporOzeti {
  id: string;
  periodStart: string;
  periodEnd: string;
  sentAt: string | null;
  readByParentAt: string | null;
  status: string;
  /**
   * PDF üretildi mi. MEVCUT-01: sunucu eskiden depo ANAHTARINI (`pdfUrl`) gönderiyordu ve portal onu
   * `href` yapıyordu — bağlantı değil, indirme hiç çalışmıyordu. Bağlantı tıklamada `veliRaporPdf`ten.
   */
  pdfVar: boolean;
}

/** Veli raporu PDF'i — süreli (15 dk) imzalı bağlantı; tıklamada istenir (MEVCUT-01). */
export function veliRaporPdf(raporId: string): Promise<{ url: string; gecerlilikSaniye: number }> {
  return api<{ url: string; gecerlilikSaniye: number }>(
    `/veli/raporlar/${encodeURIComponent(raporId)}/pdf`,
  );
}

export function veliRaporlar(ogrenciId: string): Promise<VeliRaporOzeti[]> {
  return api<VeliRaporOzeti[]>(`/veli/ogrenci/${ogrenciId}/raporlar`);
}

export interface VeliRapor {
  id: string;
  periodStart: string;
  periodEnd: string;
  draftContent: string | null;
  generalComment: string | null;
  positives: string | null;
  improvements: string | null;
  nextGoals: string | null;
  parentAdvice: string | null;
  dataCoverage: number | null;
  parentComment: string | null;
  /**
   * Raporu hazırlayan uzmanın adı.
   *
   * `null` OLABİLİR: uzmanın kullanıcı kaydı arşivlenmiş olabilir.
   * Sunucu yer tutucu bir ad ÜRETMİYOR (olmayan bir ölçümü varmış
   * gibi göstermek olurdu), yani ekran da uydurmamalı.
   */
  hazirlayan: string | null;
}

export function veliRapor(raporId: string): Promise<VeliRapor> {
  return api<VeliRapor>(`/veli/raporlar/${raporId}`);
}

export function veliYorumYaz(raporId: string, yorum: string): Promise<{ kaydedildi: true }> {
  return api<{ kaydedildi: true }>(`/veli/raporlar/${raporId}/yorum`, {
    yontem: "POST",
    govde: { yorum },
  });
}

export interface VeliGorusme {
  id: string;
  kind: string;
  scheduledAt: string;
  durationMin: number | null;
  /** Görünürlük kapısından geçmediyse `null` */
  summary: string | null;
}

export function veliGorusmeler(ogrenciId: string): Promise<VeliGorusme[]> {
  return api<VeliGorusme[]>(`/veli/ogrenci/${ogrenciId}/gorusmeler`);
}

/* ================================================================
   MESAJLAŞMA — F-MSG
   ================================================================
   Kapsam SUNUCUDA: `kutum` ve `yazisma` yalnızca kişinin KENDİ
   yazışmalarını döndürüyor. Burada hiçbir süzgeç yok — olsaydı kapsam
   iki eve dağılır ve arayüz süzgeci "güvenlik" sanılırdı.

   ⚠️ Öğrenci ve veliye PERSONEL REHBERİ VERİLMİYOR: `/mesaj/kisiler`
   onlara yalnızca kendi koçunu döndürüyor. Bu sunucunun kararı, bu
   dosyanın değil.
   ================================================================ */

export type MesajKanali = "KOC" | "KURUM" | "PERSONEL";

export interface YazismaOzeti {
  id: string;
  kanal: MesajKanali;
  baslik: string | null;
  /** KURUM kanalında "Kurum" yazar — yanıtlayan kişi değişebilir. */
  karsiTaraf: string;
  sonMesajAt: string;
  okunmamis: number;
  arsivlendiMi: boolean;
}

export interface YazismaMesaji {
  id: string;
  gonderenId: string;
  gonderenAd: string;
  /** Geri alınmışsa `null`. Gövde veritabanında DURUYOR. */
  govde: string | null;
  geriAlindiMi: boolean;
  createdAt: string;
  benimMi: boolean;
  /** Ek — F-MSG-03. Geri alınmışsa `null`. */
  ek: MesajEki | null;
}

export function mesajKutum(): Promise<{ yazismalar: YazismaOzeti[]; toplamOkunmamis: number }> {
  return api("/mesaj/kutum");
}

export function mesajYazisma(id: string): Promise<{
  id: string;
  kanal: MesajKanali;
  baslik: string | null;
  /** Arşivlenmişse yeni mesaj gönderilemez — F-MSG-07. */
  arsivlendiMi: boolean;
  /** Karşı tarafın mesai notu — F-MSG-05. Tanımlı değilse `null`. */
  mesai: { icindeMi: boolean; metin: string } | null;
  mesajlar: YazismaMesaji[];
}> {
  return api(`/mesaj/yazisma/${encodeURIComponent(id)}`);
}

export function mesajOkundu(id: string): Promise<{ okunduAt: string }> {
  return api(`/mesaj/yazisma/${encodeURIComponent(id)}/okundu`, { yontem: "POST" });
}

export interface YazilabilirKisiler {
  koc: Array<{ userId: string; ad: string; iliski: string }>;
  personel: Array<{ userId: string; ad: string }>;
  /** Öğrenci/veli kurum kanalını AÇABİLİR; personel yalnızca yanıtlar. */
  kurumAcabilirMi: boolean;
}

export function mesajKisileri(): Promise<YazilabilirKisiler> {
  return api("/mesaj/kisiler");
}

export function mesajGonder(veri: {
  kanal: MesajKanali;
  aliciId?: string | null;
  conversationId?: string | null;
  govde: string;
}): Promise<{ conversationId: string; messageId: string }> {
  return api("/mesaj/gonder", { yontem: "POST", govde: veri });
}

export function mesajGeriAl(messageId: string): Promise<{ messageId: string }> {
  return api(`/mesaj/mesaj/${encodeURIComponent(messageId)}/geri-al`, { yontem: "POST" });
}

/** Mesaj ekinin ekranda gösterilen bilgisi — içerik YOK, yalnızca üst veri. */
export interface MesajEki {
  ad: string;
  mime: string;
  boyut: number;
}

/**
 * Ekli mesaj gönderir — F-MSG-03.
 *
 * Ek AYRI bir adımda değil mesajla BİRLİKTE gidiyor: ayrı olsaydı
 * yarıda bırakılan yüklemeler sahipsiz dosya bırakır ve bir yazışmaya
 * yüklenen ekin başka bir yazışmaya iliştirilmesi mümkün olurdu.
 */
export function mesajEkliGonder(veri: {
  kanal: MesajKanali;
  aliciId?: string | null;
  conversationId?: string | null;
  govde: string;
  dosya: File;
}): Promise<{ conversationId: string; messageId: string }> {
  const govde = new FormData();
  govde.append("dosya", veri.dosya);
  govde.append("kanal", veri.kanal);
  if (veri.aliciId) govde.append("aliciId", veri.aliciId);
  if (veri.conversationId) govde.append("conversationId", veri.conversationId);
  if (veri.govde.trim()) govde.append("govde", veri.govde);
  return api("/mesaj/gonder-ek", { yontem: "POST", govde });
}

/**
 * Ekin SÜRELİ indirme bağlantısı — F-MSG-03.
 *
 * Bağlantı 15 dakika yaşıyor ve her istekte yeniden üretiliyor; kalıcı
 * bir adres verilseydi paylaşılan bir bağlantı yetki kontrolünü
 * tamamen atlardı.
 */
export function mesajEkBaglantisi(messageId: string): Promise<{ url: string; ad: string }> {
  return api(`/mesaj/mesaj/${encodeURIComponent(messageId)}/ek`);
}

/** Kendi mesai saatlerim — F-MSG-05. `kocMu` false ise ekran bölümü çizilmiyor. */
export function mesaimiGetir(): Promise<{ araliklar: MesaiAraligi[]; kocMu: boolean }> {
  return api("/mesaj/mesai");
}

export function mesaimiYaz(araliklar: MesaiAraligi[]): Promise<{ araliklar: MesaiAraligi[] }> {
  return api("/mesaj/mesai", { yontem: "POST", govde: { araliklar } });
}

// =====================================================================
// BİLDİRİM — F-NTF-09, panel içi gelen kutusu
// =====================================================================

/**
 * ================================================================
 * BU UÇLAR PANELE ÖZEL DEĞİL — HEPSİ `@KimlikGerekli()`
 * ================================================================
 * `bildirim.controller.ts` hiçbir uçta izin sormuyor: kullanıcı
 * yalnızca KENDİ bildirimlerini okuyor ve kimlik gövdeden değil
 * bağlamdan alınıyor. Yani öğrenci ve veli bu uçları bugün de
 * çağırabiliyordu; eksik olan tek şey onları çağıran bir ekrandı.
 *
 * F-NTF-09'un yazılma sebebi tam olarak bu: *"kanal veritabanına yazan
 * ama kimseye ulaşmayan bir kayıttır."*
 */
export interface Bildirim {
  id: string;
  eventCode: string;
  title: string;
  body: string | null;
  linkUrl: string | null;
  isRead: boolean;
  createdAt: string;
}

export interface BildirimSayfasi {
  bildirimler: Bildirim[];
  /** `null` = daha eski bildirim YOK. Boş dizgi DEĞİL. */
  sonrakiImlec: string | null;
}

/**
 * İMLEÇLİ sayfalama — ofset DEĞİL.
 *
 * `skip` ile sayfalamak, araya yeni bildirim düştüğünde sayfa
 * sınırlarını kaydırır: kullanıcı aynı kaydı iki kez görür ya da hiç
 * görmez. Bildirim akışı tam da sürekli başa yeni kayıt düşen bir
 * liste.
 */
export function bildirimlerim(yalnizOkunmamis = false, imlec?: string): Promise<BildirimSayfasi> {
  return api<BildirimSayfasi>("/bildirimler", {
    sorgu: { okunmamis: yalnizOkunmamis ? "1" : undefined, imlec },
  });
}

/**
 * Okunmamış sayısı — rozet için.
 *
 * Liste ucu sayfa başına sınırlı; saymak için onu çekmek hem YANLIŞ
 * sayı verir hem de yalnızca saymak için satır taşır. Panelde aynı
 * hata canlı ölçülmüştü (566 okunmamışta "100").
 */
export function bildirimSayaci(): Promise<{ okunmamis: number }> {
  return api("/bildirimler/sayac");
}

export function bildirimOkundu(id: string): Promise<{ okundu: true }> {
  return api(`/bildirimler/${encodeURIComponent(id)}/okundu`, { yontem: "POST" });
}

export function bildirimHepsiOkundu(): Promise<{ adet: number }> {
  return api("/bildirimler/hepsi-okundu", { yontem: "POST" });
}

// =====================================================================
// PROFİL GÖRSELİ — F-PPL-13
// =====================================================================

/**
 * ================================================================
 * BU UÇLAR ÖĞRENCİYE DE AÇIKTI — ÇAĞIRAN EKRAN YOKTU
 * ================================================================
 * `avatar.controller.ts` hiçbir uçta izin sormuyor: kişi yalnızca
 * KENDİ görselini değiştiriyor ve kimlik gövdeden değil bağlamdan
 * alınıyor. Yani öğrenci ve veli bunları 04.09.2026'dan beri
 * çağırabiliyordu; eksik olan tek şey onları çağıran bir ekrandı.
 *
 * F-NTF-09'un (bildirim gelen kutusu) birebir aynı sınıfı: mekanizma
 * var, ona giden yol yok.
 */
export interface AvatarGorunumu {
  /** Yüklenmiş fotoğraf VAR MI — bağlantı ayrı uçtan, gösterilecek anda. */
  fotoVarMi: boolean;
  avatarPreset: number | null;
  /** Görsel yoksa gösterilecek harfler. Boş olabilir. */
  basHarfler: string;
}

export function avatarim(): Promise<AvatarGorunumu> {
  return api<AvatarGorunumu>("/avatar");
}

export function hazirAvatarSayisi(): Promise<{ adet: number }> {
  return api("/avatar/hazir");
}

/** `null` seçimi kaldırır ve baş harflere döner. */
export function hazirAvatarSec(secim: number | null): Promise<AvatarGorunumu> {
  return api<AvatarGorunumu>("/avatar/hazir", { yontem: "POST", govde: { secim } });
}

/**
 * Kendi fotoğrafımın SÜRELİ bağlantısı.
 *
 * 15 dakika yaşıyor ve her istekte yeniden üretiliyor; kalıcı bir
 * adres verilseydi paylaşılan bir bağlantı yetki kontrolünü tamamen
 * atlardı.
 */
export function avatarimFotoBaglantisi(): Promise<{ url: string; gecerlilikSaniye: number }> {
  return api("/avatar/foto");
}

/** jpeg / png / webp, en fazla 2 MB. Eskisinin yerine geçer. */
export function avatarFotoYukle(dosya: File): Promise<AvatarGorunumu> {
  const govde = new FormData();
  govde.append("dosya", dosya);
  return api<AvatarGorunumu>("/avatar/foto", { yontem: "POST", govde });
}

/**
 * Kendi veri paketim. Eski API'de (`veri-paketim` yok — Codex tur 32 [P2]) eski iki adımlı yola düşer: eski API'de kilit
 * olmadığı için `/ogrenciler/ben` erişilebilir. Yeni API'nin `kod`lu 404'ü (`OGRENCI_DEGIL`) düşürmez.
 */
export async function veriPaketim(): Promise<unknown> {
  try {
    return await api("/kvkk/veri-paketim");
  } catch (hata) {
    if (!eskiApiYoluYokMu(hata)) throw hata;
    const kayit = await kendiKaydim();
    return veriPaketi(kayit.id);
  }
}

// =====================================================================
// BILDIRIM TERCIHI — F-NTF-03 (Hat 3, Emre 05.10.2026)
// =====================================================================

export interface BildirimTercihi {
  eventCode: string;
  channel: string;
  enabled: boolean;
}

/** YALNIZ kayitli tercihler; listede olmayan olay varsayilan ACIK. */
export function bildirimTercihlerim(): Promise<BildirimTercihi[]> {
  return api("/bildirimler/tercihler");
}

/** Kimlik oturumdan; govdede kimlik yok — baskasinin tercihi yazilamaz. */
export function bildirimTercihi(olay: string, kanal: string, acik: boolean): Promise<unknown> {
  return api("/bildirimler/tercih", { yontem: "POST", govde: { olay, kanal, acik } });
}

// =====================================================================
// GÖRÜŞMELER — F-SES-02b (Emre 06.10.2026): "Görüşmeye katıl"
// =====================================================================

export interface KendiGorusmem {
  id: string;
  kind: string;
  status: string;
  scheduledAt: string;
  durationMin: number | null;
  /** Sunucu görünürlük kuralından geçmiş bağlantı; yoksa `null` */
  meetUrl: string | null;
}

/** Öğrencinin KENDİ görüşmeleri — kapsam SELF; başka öğrencinin kimliği 403/boş döner. */
export function gorusmelerim(ogrenciId: string): Promise<KendiGorusmem[]> {
  return api<KendiGorusmem[]>(`/gorusmeler/ogrenci/${ogrenciId}`);
}

export interface VeliYaklasanGorusme {
  id: string;
  kind: string;
  scheduledAt: string;
  durationMin: number | null;
  /** YALNIZ veli görüşmesinde dolu (F-SES-02b) */
  meetUrl: string | null;
}

export function veliYaklasanGorusmeler(ogrenciId: string): Promise<VeliYaklasanGorusme[]> {
  return api<VeliYaklasanGorusme[]>(`/veli/ogrenci/${ogrenciId}/yaklasan-gorusmeler`);
}

// =====================================================================
// DUYURULAR — F-NTF-08b (Emre 06.10.2026): koçun toplu duyurusu
// =====================================================================

export interface Duyurum {
  id: string;
  title: string;
  body: string;
  linkUrl: string | null;
  publishAt: string;
  isPinned: boolean;
  okunduMu: boolean;
}

/** Sunucu yalnız bu kişiye açık duyuruları döndürür (hedef kitle kuralı sunucuda). */
export function duyurularim(): Promise<{ duyurular: Duyurum[] }> {
  return api<{ duyurular: Duyurum[] }>("/operasyon/duyurular");
}

export function duyuruOkundu(id: string): Promise<{ okundu: true }> {
  return api(`/operasyon/duyurular/${encodeURIComponent(id)}/okundu`, { yontem: "POST" });
}

// =====================================================================
// GRAFİKLER — dalga 3 (F-EXM-09, F-PAR-03)
// =====================================================================

/**
 * Deneme net trendi — panelle AYNI uç. Öğrenci kendi kaydını (SELF), veli
 * portal erişimi açık bağlı çocuğunu (LINKED) görür; kapsam sunucuda.
 * Hedef net (F-EXM-09, Emre kararı 07.10.2026): koç girer; öğrenci ve veli YALNIZ görür.
 * Satırdaki `hedefNet` o sınav türünün hedefi yoksa `null`/yok.
 */
export interface DenemeTrendim {
  denemeler: Array<{
    tarih: string;
    net: number;
    tur: string;
    ad: string;
    hedefNet?: DenemeHedefFarki | null;
  }>;
}

/** F-EXM-09 — salt okur: öğrenci SELF, veli LINKED (`deneme.hedef.goruntule`). Yazma ucu bu uygulamada YOK. */
export function hedefNetim(ogrenciId: string): Promise<HedefNetYaniti> {
  return api<HedefNetYaniti>(`/denemeler/ogrenci/${ogrenciId}/hedef-net`);
}

export function denemeTrendi(ogrenciId: string, son = 20): Promise<DenemeTrendim> {
  return api<DenemeTrendim>(`/denemeler/ogrenci/${ogrenciId}/trend`, { sorgu: { son } });
}

// ---------------------------------------------------------------------------
// ODULLER — F-GAM dalga 1 (puan defteri + rozet). Yalniz OGRENCI: veli uygulamasi
// bu uclari HIC cagirmaz (Emre S12; `oyun-bekci.test.ts` olcer).

/** Ogrencinin kendi puan ozeti. Modul kapaliysa 404 (`BULUNAMADI`). */
export function oyunOzetim(): Promise<OyunOgrenciOzeti> {
  return api("/oyun/ben");
}

/** F-GAM-11 — Instagram tesekkuru; omur boyu bir kez, dogrulanmaz. */
export function instagramTesekkuru(): Promise<PuanHareketiOzeti> {
  return api("/oyun/kampanya/instagram", { yontem: "POST" });
}

// =====================================================================
// CANLI CALISMA IZLEME — F-LIV-05/07: yalniz MODUL DURUMU. Canli LISTE ucu (`GET /canli-izleme`)
// bu uygulamadan HIC cagrilmaz: ogrenci ve veli listeyi goremez, yalniz bilgilendirilir.
// =====================================================================

/** Giris yapmis her kullanici sorabilir; kapaliysa not ve baglanti gorunmez. */
export function canliModulDurumu(): Promise<{ acik: boolean }> {
  return api("/canli-izleme/modul");
}
